import cx from 'classnames';
import { type FC, useEffect, useState } from 'react';

import {
  type PaymentMethod,
  PaymentMethodType,
  paymentMethodsRequest,
} from 'api/payments';
import Icon from 'components/Icon/Icon';
import Loader from 'components/Loader';
import PaymentItem from 'components/PaymentItem';
import { useClientProfile } from 'store';
import { getMediaUrl } from 'utils/assets';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useLazyRequest from 'utils/useLazyRequest';

import PaymentForm from 'components/PaymentForm';
import type { PopupProps } from '../types';
import styles from './PaymentPopup.module.scss';

const emptyPaymentMethods: Array<PaymentMethod> = [];

const QuickDepositPopup: FC<PopupProps> = ({ closePopup }) => {
  const [{ sessionToken }] = useClientProfile();
  const layout = useLayout();
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(
    null,
  );
  const isMobile = layout === LayoutType.mobile;

  const {
    data: response,
    loading,
    execute,
  } = useLazyRequest(paymentMethodsRequest);

  const paymentMethods = response?.data || emptyPaymentMethods;

  useEffect(() => {
    execute({
      body: {
        operationType: PaymentMethodType.DEPOSIT,
        deviceType: isMobile ? 'Mobile' : 'Desktop',
      },
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    });
  }, [sessionToken, execute, isMobile]);

  const renderLoader = loading === LoadingState.loading;
  const renderError = loading === LoadingState.failed;
  const renderEmptyState =
    paymentMethods.length === 0 && loading === LoadingState.success;

  return (
    <div
      className={cx(styles.wrapper, styles['payment-methods'], {
        [styles.mobile]: isMobile,
        [styles['single-method-wrapper']]: selectedPayment,
      })}
    >
      <button
        type="button"
        className={styles['close-button']}
        onClick={closePopup}
      >
        <Icon name="close.outline" width={24} height={24} />
      </button>

      {!selectedPayment && (
        <div className={styles.header}>
          <Icon name="payment-methods" />
          <Translation message="payment_methods" />
        </div>
      )}

      {paymentMethods.length > 0 && !selectedPayment && (
        <div className={styles.list}>
          {paymentMethods.map((payment) => (
            <PaymentItem
              key={payment.id}
              id={payment.id}
              name={payment.name}
              image={payment.desktopImageLink}
              minAmount={payment.minAmount}
              maxAmount={payment.maxAmount}
              icon={<Icon name="arrow-right" width={32} height={32} />}
              method={PaymentMethodType.DEPOSIT}
              className={styles.item}
              onClick={(e) => {
                e.preventDefault();
                setSelectedPayment(payment);
              }}
            />
          ))}
        </div>
      )}

      {renderLoader && <Loader />}

      {(renderEmptyState || renderError) && (
        <div className={styles['empty-state']}>
          <img
            width={140}
            height={140}
            alt="empty"
            src={getMediaUrl('/yellow/empty-documents.png')}
          />
          <Translation message="no_payment_methods" />
        </div>
      )}

      {selectedPayment && (
        <PaymentForm
          className={styles.form}
          method={PaymentMethodType.DEPOSIT}
          selectedPayment={selectedPayment}
        />
      )}
    </div>
  );
};

export default QuickDepositPopup;
